import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Clock, Target, Keyboard, AlertCircle, Loader2, RefreshCw, Send, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// --- Helpers ---
const computeWPM = (correctChars, elapsedSec) => {
  if (elapsedSec <= 0) return 0;
  // Standard formula: WPM = (correct chars / 5) / minutes
  return (correctChars / 5) / (elapsedSec / 60);
};
const computeAccuracy = (correctChars, totalTyped) => {
  if (totalTyped <= 0) return 100;
  return (correctChars / totalTyped) * 100;
};
const formatTime = (sec) => {
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const TypingTest = () => {
  const { passageId } = useParams();
  const navigate = useNavigate();

  const [passage, setPassage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Test state
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [typed, setTyped] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState(''); // 'saved' | 'guest' | 'error' | ''

  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    document.title = 'Typing Test — Vidya365';
    const fetchPassage = async () => {
      try {
        const res = await fetch(`${API_URL}/api/typing/passages/${passageId}`);
        if (!res.ok) throw new Error('Passage not found');
        const data = await res.json();
        setPassage(data);
        setTimeLeft(data.duration_seconds);
      } catch (e) {
        setError(e.message || 'Failed to load test');
      } finally {
        setLoading(false);
      }
    };
    fetchPassage();
  }, [passageId]);

  // Live metrics
  const metrics = useMemo(() => {
    if (!passage) return { correct: 0, wrong: 0, totalTyped: 0, wpm: 0, accuracy: 100, progress: 0 };
    const target = passage.content;
    let correct = 0;
    let wrong = 0;
    for (let i = 0; i < typed.length; i++) {
      if (i < target.length && typed[i] === target[i]) correct++;
      else wrong++;
    }
    const totalTyped = typed.length;
    const elapsed = startedAt ? (Date.now() - startedAt) / 1000 : 0;
    const wpm = computeWPM(correct, elapsed);
    const accuracy = computeAccuracy(correct, totalTyped);
    const progress = (totalTyped / target.length) * 100;
    return { correct, wrong, totalTyped, wpm, accuracy, progress };
  }, [typed, passage, startedAt]);

  // Submit attempt
  const submitAttempt = useCallback(async (m, durationSec) => {
    if (!passage) return;
    setSubmitting(true);
    const payload = {
      passage_id: passage.id,
      wpm: parseFloat(m.wpm.toFixed(2)),
      accuracy: parseFloat(m.accuracy.toFixed(2)),
      correct_chars: m.correct,
      wrong_chars: m.wrong,
      total_typed: m.totalTyped,
      duration_seconds: Math.max(1, Math.round(durationSec)),
    };

    const token = localStorage.getItem('studentToken');
    if (!token) {
      // Guest user — show result locally, prompt to login for history
      try {
        sessionStorage.setItem(
          `typing-result:${passage.id}`,
          JSON.stringify({
            ...payload,
            passage_title: passage.title,
            exam_name: passage.exam_name,
            language: passage.language,
            target_wpm: passage.target_wpm,
            passed: payload.wpm >= passage.target_wpm,
            submitted_at: new Date().toISOString(),
            guest: true,
          })
        );
      } catch (e) { /* noop */ }
      setSaveStatus('guest');
      setSubmitting(false);
      navigate(`/typing-test/${passage.id}/result?guest=1`);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/typing/attempts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      try {
        sessionStorage.setItem(`typing-result:${passage.id}`, JSON.stringify(saved));
      } catch (e) { /* noop */ }
      setSaveStatus('saved');
      navigate(`/typing-test/${passage.id}/result`);
    } catch (e) {
      setSaveStatus('error');
    } finally {
      setSubmitting(false);
    }
  }, [passage, navigate]);

  // Timer
  useEffect(() => {
    if (!started || finished) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [started, finished]);

  // Auto-submit when finished
  useEffect(() => {
    if (finished && passage && !submitting && saveStatus === '') {
      const elapsed = passage.duration_seconds - timeLeft;
      submitAttempt(metrics, elapsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  // Auto-complete if typed equals content
  useEffect(() => {
    if (started && !finished && passage && typed.length >= passage.content.length) {
      clearInterval(timerRef.current);
      setFinished(true);
    }
  }, [typed, passage, started, finished]);

  // Auto-scroll the passage viewer to keep current char visible
  useEffect(() => {
    if (!scrollerRef.current) return;
    const el = scrollerRef.current.querySelector('[data-current="true"]');
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [typed]);

  const handleStart = () => {
    setStarted(true);
    setStartedAt(Date.now());
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
  };

  const handleRestart = () => {
    clearInterval(timerRef.current);
    setTyped('');
    setStarted(false);
    setFinished(false);
    setStartedAt(null);
    setSaveStatus('');
    setTimeLeft(passage.duration_seconds);
  };

  const handleManualSubmit = () => {
    if (!started) return;
    clearInterval(timerRef.current);
    setFinished(true);
  };

  const handleKeyDown = (e) => {
    if (!started || finished) return;

    // Block clipboard shortcuts entirely
    if (e.ctrlKey || e.metaKey) {
      if (['v', 'V', 'c', 'C', 'x', 'X', 'a', 'A', 'z', 'Z'].includes(e.key)) {
        e.preventDefault();
      }
      return;
    }

    // Prevent tab from leaving the input area
    if (e.key === 'Tab') {
      e.preventDefault();
      return;
    }

    // Backspace removes last char
    if (e.key === 'Backspace') {
      e.preventDefault();
      setTyped((prev) => prev.slice(0, -1));
      return;
    }

    // Accept only single printable characters (incl. space) — blocks any
    // autocomplete / autocorrect / double-space-to-period injection because
    // those go through onBeforeInput/onChange, not onKeyDown.
    if (e.key.length === 1 && !e.altKey) {
      e.preventDefault();
      if (passage && typed.length >= passage.content.length) return;
      setTyped((prev) => prev + e.key);
    }
  };

  // Hard-block onBeforeInput so mobile IME / autocomplete cannot inject
  // characters that bypass keydown. Real keystrokes are handled by onKeyDown.
  const handleBeforeInput = (e) => {
    e.preventDefault();
  };

  const handlePaste = (e) => e.preventDefault();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
        </div>
      </div>
    );
  }

  if (error || !passage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <AlertCircle className="mx-auto text-red-600 mb-3" size={48} />
          <p className="text-gray-700 mb-4">{error || 'Test not found'}</p>
          <Link to="/typing-test"><Button data-testid="back-to-list">Back to Tests</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="container mx-auto px-4 py-6 max-w-5xl">
        {/* Heading */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <Badge className="bg-red-100 text-red-700 mb-1">{passage.exam_name}</Badge>
            <h1 className="text-2xl font-bold" data-testid="typing-title">{passage.title}</h1>
            <p className="text-xs text-gray-500">{passage.language} • Target {passage.target_wpm} WPM • {Math.round(passage.duration_seconds / 60)} min</p>
          </div>
          <Link to="/typing-test" className="text-sm text-red-600 hover:underline">← All Tests</Link>
        </div>

        {/* Live Metrics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="metrics-bar">
          <MetricPill icon={<Clock size={16} />} label="Time" value={formatTime(timeLeft)} highlight={timeLeft < 60} />
          <MetricPill icon={<Keyboard size={16} />} label="WPM" value={metrics.wpm.toFixed(1)} testid="metric-wpm" />
          <MetricPill icon={<Target size={16} />} label="Accuracy" value={`${metrics.accuracy.toFixed(1)}%`} testid="metric-accuracy" />
          <MetricPill label="Correct" value={metrics.correct} className="text-green-600" />
          <MetricPill label="Errors" value={metrics.wrong} className="text-red-600" testid="metric-errors" />
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
          <div
            className="bg-red-600 h-2 transition-all"
            style={{ width: `${Math.min(100, metrics.progress)}%` }}
            data-testid="progress-bar"
          />
        </div>

        {/* Passage display */}
        <Card className="mb-4">
          <CardContent className="p-6">
            <div
              ref={scrollerRef}
              className="font-mono text-lg leading-relaxed select-none max-h-72 overflow-y-auto overflow-x-hidden bg-gray-50 p-4 rounded border whitespace-pre-wrap break-words"
              data-testid="passage-display"
              style={{ fontFamily: passage.language === 'Hindi' ? 'system-ui, sans-serif' : 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-word' }}
              onClick={() => inputRef.current && inputRef.current.focus()}
            >
              {passage.content.split('').map((char, i) => {
                let cls = 'text-gray-400';
                let cur = false;
                if (i < typed.length) {
                  cls = typed[i] === char ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-100 underline';
                } else if (i === typed.length) {
                  cls = 'bg-yellow-200 text-gray-900 border-b-2 border-red-600 animate-pulse';
                  cur = true;
                }
                return (
                  <span key={i} className={cls} data-current={cur}>
                    {char}
                  </span>
                );
              })}
            </div>

            {/* Hidden / interactive textarea */}
            {!finished && (
              <textarea
                ref={inputRef}
                value={typed}
                onChange={() => { /* controlled by onKeyDown to block autocomplete */ }}
                onKeyDown={handleKeyDown}
                onBeforeInput={handleBeforeInput}
                onPaste={handlePaste}
                onDrop={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
                disabled={!started}
                placeholder={started ? 'Type the passage above…' : 'Click "Start Test" to begin'}
                className="w-full mt-4 p-3 border-2 border-gray-300 rounded-md focus:border-red-500 focus:outline-none font-mono text-base"
                rows={3}
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
                autoComplete="off"
                inputMode="text"
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
                data-testid="typing-input"
              />
            )}
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 justify-center">
          {!started && !finished && (
            <Button data-testid="start-test-btn" onClick={handleStart} className="bg-red-600 hover:bg-red-700">
              <Play size={16} className="mr-2" /> Start Test
            </Button>
          )}
          {started && !finished && (
            <>
              <Button data-testid="submit-test-btn" onClick={handleManualSubmit} className="bg-green-600 hover:bg-green-700">
                <Send size={16} className="mr-2" /> Submit Test
              </Button>
              <Button data-testid="restart-test-btn" variant="outline" onClick={handleRestart}>
                <RefreshCw size={16} className="mr-2" /> Restart
              </Button>
            </>
          )}
          {finished && (
            <div className="text-center w-full">
              {submitting ? (
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <Loader2 className="animate-spin" size={20} /> Saving result…
                </div>
              ) : saveStatus === 'error' ? (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-red-600">Failed to save. Try again.</p>
                  <Button onClick={() => submitAttempt(metrics, passage.duration_seconds - timeLeft)}>Retry Save</Button>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Tip */}
        {!started && !finished && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
            <strong>Tips:</strong> The test starts when you click <em>Start Test</em>. Pasting is disabled to simulate real exam conditions.
            {!localStorage.getItem('studentToken') && (
              <> Want to save your progress and track improvement? <Link to="/student/login" className="font-semibold underline">Login</Link> or <Link to="/student/register" className="font-semibold underline">Register</Link>.</>
            )}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

const MetricPill = ({ icon, label, value, highlight, className = '', testid }) => (
  <div
    data-testid={testid}
    className={`flex items-center justify-between px-3 py-2 rounded-md border ${highlight ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200'} ${className}`}
  >
    <div className="flex items-center gap-1 text-xs text-gray-500">
      {icon}
      <span>{label}</span>
    </div>
    <span className="font-bold text-sm">{value}</span>
  </div>
);

export default TypingTest;
